import Vue from "vue";

const DEFAULT_CONFIG = {
  gapPx: 12,
  baseZIndex: 1000,
};

const state = Vue.observable({
  items: {},
  config: { ...DEFAULT_CONFIG },
  revision: 0,
});

let insertionCounter = 0;

function bumpRevision() {
  state.revision += 1;
}

function sortedVisibleItems(anchor) {
  return Object.values(state.items)
    .filter((item) => item.visible && item.anchor === anchor)
    .sort((a, b) => {
      if (a.priority !== b.priority) {
        return a.priority - b.priority;
      }

      return a.insertionOrder - b.insertionOrder;
    });
}

export function setFloatingConfig(config = {}) {
  state.config = {
    ...state.config,
    ...config,
  };
  bumpRevision();
}

export function registerFloatingItem({
  id,
  priority = 100,
  anchor = "right",
  zIndex = null,
}) {
  if (!id) {
    return;
  }

  const previous = state.items[id] || {};
  Vue.set(state.items, id, {
    id,
    priority,
    anchor,
    zIndex,
    visible: previous.visible !== undefined ? previous.visible : true,
    height: previous.height || 0,
    insertionOrder:
      previous.insertionOrder !== undefined
        ? previous.insertionOrder
        : insertionCounter++,
  });
  bumpRevision();
}

export function updateFloatingItem(id, updates = {}) {
  const item = state.items[id];
  if (!item) {
    return;
  }

  Vue.set(state.items, id, {
    ...item,
    ...updates,
  });
  bumpRevision();
}

export function unregisterFloatingItem(id) {
  if (!state.items[id]) {
    return;
  }

  Vue.delete(state.items, id);
  bumpRevision();
}

export function getFloatingItemStyle(id, localStyle = {}) {
  const item = state.items[id];
  if (!item) {
    return {};
  }

  // Access revision so Vue recomputes when item set/order/height changes.
  state.revision;
  const stack = sortedVisibleItems(item.anchor);
  const currentIndex = stack.findIndex((entry) => entry.id === id);
  const gap = state.config.gapPx || DEFAULT_CONFIG.gapPx;
  const offsetPx = stack
    .slice(0, Math.max(currentIndex, 0))
    .reduce((sum, entry) => sum + (entry.height || 0) + gap, 0);

  const baseStyle = {
    position: "fixed",
    bottom: `calc(var(--floating-stack-bottom, 12px) + ${offsetPx}px)`,
    zIndex: item.zIndex || state.config.baseZIndex || DEFAULT_CONFIG.baseZIndex,
  };

  if (item.anchor === "full") {
    baseStyle.left = 0;
    baseStyle.right = 0;
  } else if (item.anchor === "left") {
    baseStyle.left = "var(--floating-stack-inline, 20px)";
  } else {
    baseStyle.right = "var(--floating-stack-inline, 20px)";
  }

  return {
    ...baseStyle,
    ...localStyle,
  };
}
